import { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";

import { AnchoredPopup, AnchoredPopupBody, AnchoredPopupHeader } from "../AnchoredPopup";
import { SrOnly } from "../foundations/srOnly";
import { controlFocusRing, inlineElementIndicatorSize } from "../foundations/controlStyles";
import { GraphLineIcon } from "../../icons/glyphs";
import { MEDIA_TIMELINE_POLICY } from "../MediaTimelineBar/mediaTimelineMath";
import { clockScaleFor, formatClockTime, formatSignedClock } from "../../utils/clockTime";
import type { NormalizedSeries, SeriesResolvedLane } from "./seriesData";
import { segmentLineOf } from "../MediaTimelineBar/timelineSegmentPaint";
import { SERIES_BAND_HUE_PERCENT, seriesInkOf, seriesLineOf } from "./seriesCanvasPaint";
import { SeriesSwatch } from "./SeriesSwatch";
import {
  DEFAULT_SERIES_READOUT_LABELS,
  formatSeriesValue,
  readSeriesAt,
  type SeriesReadoutLabels,
} from "./seriesSampling";
import { buildSeriesPath, type SeriesPathCommand } from "./seriesPath";
import type {
  MediaTimelineSegment,
  MediaTimelineSeriesDomain,
} from "../MediaTimelineBar/timelineTypes";

/** `line` intent: a deliberate pause on a line, so seeking and scrubbing stay unobstructed. */
const LINE_HOVER_DELAY_MS = 350;
/** AnchoredPopup's anchor offset and viewport margin, which the free side's height must leave. */
const POPUP_OFFSET_PX = 6;
const POPUP_MARGIN_PX = 8;
/** Below this the popup would show only a header, so it may overlap the row instead. */
const POPUP_MIN_HEIGHT_PX = 160;
const LINE_HIT_RADIUS_PX = 6;

/** Use the painted geometry, including steep slopes, step jumps, gaps and dense envelopes. */
function isNearPath(commands: readonly SeriesPathCommand[], x: number, y: number) {
  return commands.some((point, index) => {
    const previous = commands[index - 1];
    if (point.op === "move" || !previous) return false;
    const dx = point.x - previous.x;
    const dy = point.y - previous.y;
    const lengthSquared = dx * dx + dy * dy;
    const fraction = lengthSquared
      ? Math.max(0, Math.min(1, ((x - previous.x) * dx + (y - previous.y) * dy) / lengthSquared))
      : 0;
    return (
      Math.hypot(x - previous.x - fraction * dx, y - previous.y - fraction * dy) <=
      LINE_HIT_RADIUS_PX
    );
  });
}

const Probe = styled.div`
  position: absolute;
  z-index: 2;
  inset: 0;
  pointer-events: none;
  outline: none;
  &:focus-visible {
    ${controlFocusRing("2px")}
  }
`;
const Cursor = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px dashed ${({ theme }) => theme.color.text.muted};
`;
const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  font-size: ${({ theme }) => theme.typography.size.caption};
`;
const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;
const Title = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.color.text.strong};
  font-size: ${({ theme }) => theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`;
const NumberCell = styled.div`
  flex-shrink: 0;
  text-align: right;
  color: ${({ theme }) => theme.color.text.strong};
  font-size: ${({ theme }) => theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  font-variant-numeric: tabular-nums;
`;
const Hint = styled.div`
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;
/** Source keys have no natural break points, so the caption wraps anywhere rather than overflow. */
const Caption = styled(Hint)`
  overflow-wrap: anywhere;
`;
/**
 * A lane section heading when lanes cannot share one matrix: the row's own voice, above the
 * quiet column titles, and a firmer rule than the one between value rows.
 */
const LaneHeading = styled.th`
  && {
    padding: 0.6rem 0 0.2rem;
    text-align: left;
    color: ${({ theme }) => theme.color.text.default};
    font-weight: ${({ theme }) => theme.typography.weight.semibold};
  }
  tbody + tbody tr:first-child > && {
    border-top-color: ${({ theme }) => theme.color.border.default};
  }
`;
/** A lane's own axis range, in the caption's quiet voice, under the lane name. */
const LaneRange = styled.span`
  display: block;
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
  font-variant-numeric: tabular-nums;
`;
/**
 * The axis glyph that tells a range apart from the source-key caption above it. Decorative for
 * sighted readers; the label beside it is the spoken version, so the popup carries no tooltip
 * and no extra focus stop.
 */
const AxisGlyph = styled.span`
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.3rem;
  font-size: ${inlineElementIndicatorSize.xs};
`;
function AxisRange({ label, children }: { label: string; children: string }) {
  return (
    <>
      <AxisGlyph aria-hidden="true" data-media-series-axis-glyph="">
        <GraphLineIcon />
      </AxisGlyph>
      <SrOnly>{label} </SrOnly>
      {children}
    </>
  );
}
const Values = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
  th {
    padding: 0 0 0.35rem;
    text-align: left;
    color: ${({ theme }) => theme.color.text.muted};
    font-size: ${({ theme }) => theme.typography.size.caption};
    font-weight: ${({ theme }) => theme.typography.weight.regular};
  }
  th + th {
    text-align: right;
  }
  /* Lane captions can be long; they wrap so the value columns stay inside the popup. */
  th {
    overflow-wrap: anywhere;
  }
  td {
    padding: 0.4rem 0;
    vertical-align: middle;
  }
  tr + tr td,
  tbody + tbody tr:first-child td,
  tbody + tbody tr:first-child th {
    border-top: 1px solid ${({ theme }) => theme.color.border.subtle};
  }
  td:first-child {
    overflow-wrap: anywhere;
  }
  td + td,
  th + th {
    padding-left: 1rem;
  }
  td + td {
    white-space: nowrap;
  }
`;
const Swatch = styled(SeriesSwatch)`
  margin-right: 0.45rem;
`;

interface Props {
  gutter?: number;
  /** `line` opens only near a stroke after a pause; `plot` opens anywhere in the plot. */
  hoverIntent?: "line" | "plot";
  /** Dwell before opening. Defaults to 350ms for `line` and 0 for `plot`. */
  hoverDelayMs?: number;
  unit?: string;
  onTimeSelect?: (timeSeconds: number) => void;
  selectionHint?: string;
  /** Stacked bands of one plot, top to bottom, each with its axis settled; a plain row is one unlabeled lane. */
  lanes: readonly SeriesResolvedLane[];
  bands?: readonly MediaTimelineSegment[];
  viewportStart: number;
  viewportDuration: number;
  /**
   * The latest time this surface can show, which picks the clock form of the popup time (the
   * media duration on a timeline). Defaults to the viewport's largest magnitude, since a chart can
   * run before zero; the time keeps its sign.
   */
  latestTimeSeconds?: number;
  label?: string;
  /** Caption under the popup title; blank text is not rendered. */
  description?: string;
  /**
   * The authored shared axis, captioned under the title so the range the plot is scaled to is
   * read where its values are. A plot with visible ticks leaves it out.
   */
  axisRange?: MediaTimelineSeriesDomain | null;
  suppressed: boolean;
  labels?: SeriesReadoutLabels;
}

/** An independent interaction layer: pointer updates never invalidate the series canvas. */
export const SeriesReadout = memo(function SeriesReadout({
  lanes,
  gutter = MEDIA_TIMELINE_POLICY.leftGutterPx,
  hoverIntent = "line",
  hoverDelayMs = hoverIntent === "plot" ? 0 : LINE_HOVER_DELAY_MS,
  unit,
  onTimeSelect,
  selectionHint,
  bands,
  viewportStart,
  viewportDuration,
  latestTimeSeconds = Math.max(Math.abs(viewportStart), Math.abs(viewportStart + viewportDuration)),
  label,
  description,
  axisRange = null,
  suppressed,
  labels = DEFAULT_SERIES_READOUT_LABELS,
}: Props) {
  const caption = description?.trim() || null;
  const axisText = axisRange
    ? `${formatSeriesValue(axisRange.min)}–${formatSeriesValue(axisRange.max)}${unit ? ` ${unit}` : ""}`
    : null;
  const readingLabels = {
    sample: labels.seriesSample,
    linear: labels.seriesInterpolated,
    step: labels.seriesHeld,
    gap: labels.seriesNoData,
  };
  const theme = useTheme();
  const id = useId();
  const probeRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState<HTMLSpanElement | null>(null);
  const popupContentRef = useRef<HTMLDivElement>(null);
  const [scrollBody, setScrollBody] = useState<HTMLDivElement | null>(null);
  // The row's wheel listener lives in an effect that must not rebind on every scroll-body mount.
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  const bindScrollBody = useCallback((node: HTMLDivElement | null) => {
    scrollBodyRef.current = node;
    setScrollBody(node);
  }, []);
  const [canScroll, setCanScroll] = useState(false);
  useEffect(() => {
    if (!scrollBody) return;
    const measure = () => setCanScroll(scrollBody.scrollHeight > scrollBody.clientHeight);
    const observer = new ResizeObserver(measure);
    observer.observe(scrollBody);
    if (scrollBody.firstElementChild) observer.observe(scrollBody.firstElementChild);
    measure();
    return () => observer.disconnect();
  }, [scrollBody]);
  const dismissed = useRef(false);
  const inspecting = useRef(false);
  const hoverFrame = useRef(0);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [fraction, setFraction] = useState(0.5);
  const [open, setOpen] = useState(false);
  // Listeners read the committed state; a render may not commit.
  const openRef = useRef(false);
  useLayoutEffect(() => {
    openRef.current = open;
  }, [open]);
  const close = useCallback(() => {
    clearTimeout(hoverTimer.current);
    cancelAnimationFrame(hoverFrame.current);
    hoverFrame.current = 0;
    inspecting.current = false;
    dismissed.current = true;
    setOpen(false);
  }, []);
  // Seek through the latest committed callback: an inline `onSeek` is new on every parent render,
  // and rebinding the row closes the popup, so a playing chart would close it on each time update.
  const onTimeSelectRef = useRef(onTimeSelect);
  useLayoutEffect(() => {
    onTimeSelectRef.current = onTimeSelect;
  }, [onTimeSelect]);

  useEffect(() => {
    const probe = probeRef.current;
    const row = probe?.parentElement;
    if (!row || !probe) return;
    close();
    if (suppressed) return;
    let paths: SeriesPathCommand[][] = [];
    let pathWidth = 0;
    let pathHeight = 0;
    let nextFraction = 0.5;
    const move = (event: PointerEvent) => {
      clearTimeout(hoverTimer.current);
      if (event.pointerType === "touch") return;
      if (event.buttons) {
        close();
        return;
      }
      // A fresh movement restarts intent after dismissal, even without leaving the row.
      // close() cancels pending work, so a stationary pointer cannot reopen the popup.
      dismissed.current = false;
      const pause = () => {
        cancelAnimationFrame(hoverFrame.current);
        hoverFrame.current = 0;
        inspecting.current = false;
        setOpen(false);
      };
      // Range handles retain their own value hint; the plot must not compete with it.
      if (event.target instanceof Element && event.target.closest('button, [role="slider"]')) {
        pause();
        return;
      }
      const bounds = probe.getBoundingClientRect();
      const width = probe.clientWidth;
      const height = probe.clientHeight;
      if (!(bounds.width > 0 && bounds.height > 0 && width > gutter)) {
        pause();
        return;
      }
      const x = ((event.clientX - bounds.left) / bounds.width) * width;
      const y = ((event.clientY - bounds.top) / bounds.height) * height;
      nextFraction = (x - gutter) / (width - gutter);
      if (nextFraction < 0 || nextFraction > 1 || y < 0 || y > height) {
        pause();
        return;
      }
      // Once inspection is intentional, follow the shared x continuously until dismissal.
      if (inspecting.current) {
        if (!hoverFrame.current)
          hoverFrame.current = requestAnimationFrame(() => {
            hoverFrame.current = 0;
            setFraction(nextFraction);
          });
        return;
      }
      // Wait for deliberate inspection; normal pointer movement and seeking stay unobstructed.
      hoverTimer.current = setTimeout(() => {
        if (dismissed.current) return;
        // Each lane is its own plot band: paths are built at lane height and tested in lane y.
        const laneHeight = height / lanes.length;
        if (hoverIntent === "line" && (pathWidth !== width || pathHeight !== height)) {
          paths = lanes.map((lane) =>
            lane.series.flatMap((entry) =>
              buildSeriesPath(entry, {
                gutter,
                width,
                height: laneHeight,
                domain: lane.domain,
                viewportStart,
                viewportDuration,
              }),
            ),
          );
          pathWidth = width;
          pathHeight = height;
        }
        if (
          hoverIntent === "line" &&
          !paths.some((path, index) => isNearPath(path, x, y - index * laneHeight))
        ) {
          return;
        }
        inspecting.current = true;
        setFraction(nextFraction);
        setOpen(true);
      }, hoverDelayMs);
    };
    const enter = (event: PointerEvent) => {
      dismissed.current = false;
      move(event);
    };
    const leave = close;
    const scroll = (event: Event) => {
      // Reading a long comparison may scroll its popup, which stays open.
      if (event.target instanceof Node && popupContentRef.current?.contains(event.target)) return;
      close();
    };
    // The popup follows the pointer, so it can never be wheeled directly: while it is open, a
    // vertical wheel over the row scrolls it instead of the page. Ctrl+wheel stays the bar's zoom.
    const wheel = (event: WheelEvent) => {
      const body = scrollBodyRef.current;
      if (!openRef.current || !body || event.ctrlKey || event.deltaY === 0) return;
      if (body.scrollHeight <= body.clientHeight) return;
      event.preventDefault();
      body.scrollTop += event.deltaY;
    };
    const select = (event: MouseEvent) => {
      if (!onTimeSelectRef.current || event.button !== 0 || event.detail === 0) return;
      if (event.target instanceof Element && event.target.closest('button, input, [role="slider"]'))
        return;
      const bounds = probe.getBoundingClientRect();
      const width = probe.clientWidth;
      if (!(bounds.width > 0 && width > gutter)) return;
      const x = ((event.clientX - bounds.left) / bounds.width) * width;
      const selected = Math.max(0, Math.min(1, (x - gutter) / (width - gutter)));
      onTimeSelectRef.current(viewportStart + selected * viewportDuration);
    };
    row.addEventListener("click", select);
    row.addEventListener("pointerenter", enter);
    row.addEventListener("pointermove", move);
    row.addEventListener("pointerleave", leave);
    row.addEventListener("pointerdown", close, true);
    row.addEventListener("wheel", wheel, { passive: false });
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", close);
    return () => {
      clearTimeout(hoverTimer.current);
      cancelAnimationFrame(hoverFrame.current);
      hoverFrame.current = 0;
      inspecting.current = false;
      row.removeEventListener("click", select);
      row.removeEventListener("pointerenter", enter);
      row.removeEventListener("pointermove", move);
      row.removeEventListener("pointerleave", leave);
      row.removeEventListener("pointerdown", close, true);
      row.removeEventListener("wheel", wheel);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", close);
    };
  }, [
    close,
    lanes,
    viewportStart,
    viewportDuration,
    suppressed,
    gutter,
    hoverIntent,
    hoverDelayMs,
  ]);

  const time = viewportStart + fraction * viewportDuration;
  // A single unlabeled lane reads as a flat list; captions appear once lanes are stacked.
  const grouped = lanes.length > 1;
  const groups = lanes.map((lane) => ({
    lane,
    caption: grouped ? lane.label?.trim() || lane.id : null,
    // A lane on its own axis says so where its values are read: same height no longer means
    // the same value, and the plot itself draws no scale.
    range: lane.ownDomain
      ? `${formatSeriesValue(lane.domain.min)}–${formatSeriesValue(lane.domain.max)}`
      : null,
    readings: lane.series.map((entry) => ({ entry, reading: readSeriesAt(entry, time) })),
  }));
  // One reading kind is the popup's default and sits under the time; a row only says its own
  // kind when it differs (held, no data, an exact sample), so six rows do not repeat one word.
  const kinds = groups.flatMap(({ readings }) => readings.map(({ reading }) => reading.kind));
  const defaultKind = kinds.includes("linear") ? "linear" : (kinds[0] ?? null);
  const defaultKindText = defaultKind ? readingLabels[defaultKind]?.trim() || null : null;
  const hintOf = (reading: ReturnType<typeof readSeriesAt>) =>
    reading.kind === defaultKind ? null : readingLabels[reading.kind]?.trim() || null;
  const nameOf = (entry: NormalizedSeries) => entry.label || entry.id;
  // Lanes that carry the same series (by name and order) compare as one matrix: a row per
  // series, a value column per lane. Otherwise each lane is its own section.
  const lead = groups[0];
  const matrix =
    grouped &&
    lead !== undefined &&
    lead.readings.length > 0 &&
    groups.every(
      ({ readings }) =>
        readings.length === lead.readings.length &&
        readings.every(({ entry }, index) => nameOf(entry) === nameOf(lead.readings[index]!.entry)),
    );
  // Lanes of one series each are a plain list whose rows are the lanes: a section heading over a
  // single row would only repeat the caption.
  const perLane = grouped && !matrix && groups.every(({ readings }) => readings.length === 1);
  const timeText = formatSignedClock(time, (magnitude) =>
    formatClockTime(magnitude, 3, clockScaleFor(latestTimeSeconds)),
  );
  const valueText = (reading: ReturnType<typeof readSeriesAt>) =>
    reading.value === null ? "—" : `${formatSeriesValue(reading.value)}${unit ? ` ${unit}` : ""}`;
  let band: MediaTimelineSegment | undefined;
  for (const candidate of bands ?? []) {
    if (
      candidate.startSeconds <= time &&
      candidate.endSeconds > time &&
      (!band?.active || candidate.active)
    ) {
      band = candidate;
    }
  }
  const ink = seriesInkOf(theme);
  const inkColor = band
    ? `color-mix(in srgb, ${segmentLineOf(theme, band.tone)} ${SERIES_BAND_HUE_PERCENT}%, ${ink} ${100 - SERIES_BAND_HUE_PERCENT}%)`
    : ink;
  const swatchColor = (entry: NormalizedSeries) =>
    entry.tone ? seriesLineOf(theme, entry.tone) : inkColor;
  const visible = open && !suppressed;
  // The popup follows the pointer, so it can only be read, never reached: give it the whole free
  // side of the viewport instead of a fixed cap, and let it flip to whichever side is taller.
  // Scrolling (wheel over the row, Page keys) remains for what still does not fit.
  const [popupMaxHeight, setPopupMaxHeight] = useState<string>();
  useLayoutEffect(() => {
    if (!visible) return;
    const row = probeRef.current?.parentElement;
    if (!row) return;
    const rect = row.getBoundingClientRect();
    const above = rect.top - POPUP_MARGIN_PX - POPUP_OFFSET_PX;
    const below = window.innerHeight - rect.bottom - POPUP_MARGIN_PX - POPUP_OFFSET_PX;
    setPopupMaxHeight(`${Math.max(POPUP_MIN_HEIGHT_PX, Math.floor(Math.max(above, below)))}px`);
  }, [visible]);
  return (
    <>
      {onTimeSelect && selectionHint ? <SrOnly id={`${id}-seek`}>{selectionHint}</SrOnly> : null}
      {canScroll ? <SrOnly id={`${id}-scroll`}>{labels.seriesScrollHint}</SrOnly> : null}
      <Probe
        ref={probeRef}
        role="slider"
        tabIndex={suppressed ? -1 : 0}
        aria-label={label ? `${label}: ${labels.seriesValues}` : labels.seriesValues}
        aria-valuemin={viewportStart}
        aria-valuemax={viewportStart + viewportDuration}
        aria-valuenow={time}
        aria-valuetext={`${timeText}; ${groups
          .flatMap(({ caption, readings }) =>
            readings.map(({ entry, reading }) => {
              const kind = readingLabels[reading.kind]?.trim();
              return `${caption ? `${caption} · ` : ""}${nameOf(entry)}: ${valueText(reading)}${kind ? ` (${kind})` : ""}`;
            }),
          )
          .join("; ")}`}
        aria-controls={visible ? id : undefined}
        aria-describedby={
          [
            visible && canScroll ? `${id}-scroll` : "",
            onTimeSelect && selectionHint ? `${id}-seek` : "",
          ]
            .filter(Boolean)
            .join(" ") || undefined
        }
        data-media-series-probe=""
        onFocus={() => {
          dismissed.current = false;
          setOpen(true);
        }}
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === "Enter" && onTimeSelect) {
            event.preventDefault();
            event.stopPropagation();
            onTimeSelect(time);
            return;
          }
          if ((event.key === "PageDown" || event.key === "PageUp") && visible && scrollBody) {
            event.preventDefault();
            event.stopPropagation();
            // Keep focus and the pointer on the row: leaving it must still dismiss immediately.
            scrollBody.scrollTop +=
              (event.key === "PageDown" ? 1 : -1) * scrollBody.clientHeight * 0.9;
            return;
          }
          const delta =
            event.key === "ArrowRight" || event.key === "ArrowUp"
              ? 0.01
              : event.key === "ArrowLeft" || event.key === "ArrowDown"
                ? -0.01
                : 0;
          if (!delta && event.key !== "Home" && event.key !== "End") return;
          event.preventDefault();
          event.stopPropagation();
          dismissed.current = false;
          setFraction((current) =>
            event.key === "Home"
              ? 0
              : event.key === "End"
                ? 1
                : Math.max(0, Math.min(1, current + delta)),
          );
          setOpen(true);
        }}
      >
        {visible ? (
          <Cursor
            ref={setCursor}
            aria-hidden="true"
            data-media-series-cursor=""
            style={{
              left: `calc(${gutter}px + (100% - ${gutter}px) * ${fraction})`,
            }}
          />
        ) : null}
      </Probe>
      <AnchoredPopup
        open={visible}
        anchor={visible && cursor ? { type: "rect", el: cursor, rect: new DOMRect() } : null}
        onClose={close}
        role="presentation"
        placement="top"
        width={matrix && groups.length > 2 ? "26rem" : "18rem"}
        maxHeight={popupMaxHeight}
        // Nothing in it is interactive, and the pointer must stay the row's even where the popup
        // has to overlap it.
        style={{ pointerEvents: "none" }}
        // c6 focus policy, not HTML autofocus: inspection must preserve the user's focus.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={false}
        returnFocus={false}
        repositionOnScroll={false}
      >
        <div
          ref={popupContentRef}
          id={id}
          role="region"
          aria-label={labels.seriesValues}
          style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <AnchoredPopupHeader padding="sm" divider>
            <Heading>
              <TitleBlock>
                <Title>{label ?? labels.series}</Title>
                {caption ? <Caption data-media-series-description="">{caption}</Caption> : null}
                {axisText ? (
                  <Caption data-media-series-axis-range="">
                    <AxisRange label={labels.seriesAxisRange}>{axisText}</AxisRange>
                  </Caption>
                ) : null}
              </TitleBlock>
              <NumberCell>
                <Hint>{labels.seriesTime}</Hint>
                {timeText}
                {defaultKindText ? (
                  <Hint data-media-series-reading-kind="">{defaultKindText}</Hint>
                ) : null}
              </NumberCell>
            </Heading>
          </AnchoredPopupHeader>
          <AnchoredPopupBody ref={bindScrollBody} padding="sm" scroll>
            {matrix && lead ? (
              <Values data-media-series-layout="matrix">
                <thead>
                  <tr>
                    <th scope="col">{labels.series}</th>
                    {groups.map(({ lane, caption, range }, laneIndex) => (
                      <th
                        key={lane.id || laneIndex}
                        scope="col"
                        data-media-series-lane={lane.id || undefined}
                      >
                        {caption}
                        {range ? (
                          <LaneRange data-media-series-lane-range="">
                            <AxisRange label={labels.seriesAxisRange}>{range}</AxisRange>
                          </LaneRange>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lead.readings.map(({ entry: leadEntry }, index) => (
                    <tr key={`${leadEntry.id}:${index}`}>
                      <td>
                        <Swatch
                          aria-hidden="true"
                          $lineStyle={leadEntry.lineStyle}
                          style={{ color: swatchColor(leadEntry) }}
                        />
                        {nameOf(leadEntry)}
                      </td>
                      {groups.map(({ lane, readings }, laneIndex) => {
                        const { entry, reading } = readings[index]!;
                        const hint = hintOf(reading);
                        // A lane that draws this series in its own style shows it by its value.
                        const restyled =
                          entry.tone !== leadEntry.tone || entry.lineStyle !== leadEntry.lineStyle;
                        return (
                          <td key={lane.id || laneIndex}>
                            <NumberCell>
                              {restyled ? (
                                <Swatch
                                  aria-hidden="true"
                                  $lineStyle={entry.lineStyle}
                                  style={{ color: swatchColor(entry) }}
                                />
                              ) : null}
                              {valueText(reading)}
                              {hint ? <Hint>{hint}</Hint> : null}
                            </NumberCell>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Values>
            ) : (
              <Values data-media-series-layout={perLane ? "lanes" : grouped ? "sections" : "list"}>
                <thead>
                  <tr>
                    <th scope="col">{labels.series}</th>
                    <th scope="col">{labels.seriesValue}</th>
                  </tr>
                </thead>
                {perLane ? (
                  <tbody>
                    {groups.map(({ lane, caption, range, readings }, laneIndex) => {
                      const { entry, reading } = readings[0]!;
                      const hint = hintOf(reading);
                      const name = nameOf(entry);
                      return (
                        <tr
                          key={lane.id || laneIndex}
                          data-media-series-lane={lane.id || undefined}
                        >
                          <td>
                            <Swatch
                              aria-hidden="true"
                              $lineStyle={entry.lineStyle}
                              style={{ color: swatchColor(entry) }}
                            />
                            {caption === name ? caption : `${caption} · ${name}`}
                            {range ? (
                              <LaneRange data-media-series-lane-range="">
                                <AxisRange label={labels.seriesAxisRange}>{range}</AxisRange>
                              </LaneRange>
                            ) : null}
                          </td>
                          <td>
                            <NumberCell>
                              {valueText(reading)}
                              {hint ? <Hint>{hint}</Hint> : null}
                            </NumberCell>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  groups.map(({ lane, caption, range, readings }, laneIndex) => (
                    <tbody key={lane.id || laneIndex} data-media-series-lane={lane.id || undefined}>
                      {caption ? (
                        <tr>
                          <LaneHeading scope="rowgroup" colSpan={2}>
                            {caption}
                            {range ? (
                              <LaneRange data-media-series-lane-range="">
                                <AxisRange label={labels.seriesAxisRange}>{range}</AxisRange>
                              </LaneRange>
                            ) : null}
                          </LaneHeading>
                        </tr>
                      ) : null}
                      {readings.map(({ entry, reading }, index) => {
                        const hint = hintOf(reading);
                        return (
                          <tr key={`${entry.id}:${index}`}>
                            <td>
                              <Swatch
                                aria-hidden="true"
                                $lineStyle={entry.lineStyle}
                                style={{ color: swatchColor(entry) }}
                              />
                              {nameOf(entry)}
                            </td>
                            <td>
                              <NumberCell>
                                {valueText(reading)}
                                {hint ? <Hint>{hint}</Hint> : null}
                              </NumberCell>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ))
                )}
              </Values>
            )}
          </AnchoredPopupBody>
        </div>
      </AnchoredPopup>
    </>
  );
});
